import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Target, Loader2, ChevronDown, ChevronRight } from 'lucide-react';

// ============================================================================
// Best fit right now - which open roles this person actually suits
// ============================================================================
//
// WHAT THIS IS
// ------------
// The Matches screen serves rows that were scored months ago by the old
// scoring code. Three of its five axes were near-constants, so almost
// everybody came out at 80-95% and the number stopped meaning anything.
//
// This panel does not read those stored rows at all. It asks the backend to
// score this person against the open requirements right now, using the
// rebuilt engine in routes/dcs_match.py, and shows what came back.
//
// FOUR RULES
// ----------
//
// 1. IT DISAPPEARS WHEN IT IS EMPTY. A candidate with no open requirement
//    worth showing renders null - not an empty card, not a "no matches"
//    row. A panel that is present on every profile and useful on one in ten
//    is noise.
//
// 2. A FAILED FETCH HIDES THE PANEL, IT DOES NOT BREAK THE PAGE. If the
//    scoring call fails the profile around it must still work, so the panel
//    simply does not appear - exactly as it does for someone with no fits.
//
// 3. NO NUMBER WITHOUT ITS REASON. Every score is shown with the plain
//    sentence the engine gave for each axis, and with its confidence. A bare
//    percentage is precisely what failed the first time, so this panel is
//    built so that it cannot show one.
//
// 4. IT WRITES NOTHING. The endpoint behind it is read-only. Opening this
//    panel does not change a single stored row.

// House style. Copied rather than imported, same as the other new DCS panels.
const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5',
  ink: '#2b2b2b', muted: '#7a7a7a', gold: '#c9a84c', goldDark: '#b08d36',
  navy: '#2e3a68', warn: '#8a6d1f', warnBg: '#fdf6e3',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const errText = (e) =>
  e?.response?.data?.detail || e?.message || 'Could not score this candidate';

/* Strong enough to act on, worth a look, or only technically a match. */
const band = (n) => {
  if (n >= 70) return { label: 'strong fit', fg: '#1f5f3f', bg: '#eaf5ee' };
  if (n >= 45) return { label: 'worth a look', fg: C.warn, bg: C.warnBg };
  return { label: 'weak fit', fg: C.muted, bg: '#f4f2ec' };
};

/* The engine hands back one sentence per axis it could actually score. They
   are shown in the order a recruiter would ask them, not alphabetically. */
const WHY_ORDER = [
  ['family', 'Field'],
  ['skills', 'Skills'],
  ['experience', 'Experience'],
  ['location', 'Location'],
  ['money', 'Money'],
];

export function ScoreBadge({ score, confidence }) {
  const b = band(score);
  return (
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <div style={{
        display: 'inline-block', padding: '4px 10px', borderRadius: 999,
        background: b.bg, color: b.fg, font: `600 15px ${FONT}`,
        whiteSpace: 'nowrap',
      }}>
        {Math.round(score)}% · {b.label}
      </div>
      <div style={{ font: `400 11px ${FONT}`, color: C.muted, marginTop: 4 }}>
        {confidence} confidence
      </div>
    </div>
  );
}

export function WhyList({ why, matched, missing }) {
  const lines = WHY_ORDER.filter(([k]) => why?.[k]);
  return (
    <div style={{ marginTop: 8 }}>
      {lines.map(([k, label]) => (
        <div key={k} style={{ font: `400 12.5px ${FONT}`, color: C.ink, marginTop: 2 }}>
          <span style={{ color: C.muted, display: 'inline-block', minWidth: 78 }}>
            {label}
          </span>
          {why[k]}
        </div>
      ))}
      {(matched?.length > 0 || missing?.length > 0) && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {(matched || []).map((s) => (
            <span key={`m${s}`} style={{
              font: `500 11px ${FONT}`, color: '#1f5f3f', background: '#eaf5ee',
              border: '1px solid #cfe6d8', borderRadius: 4, padding: '2px 7px',
            }}>{s}</span>
          ))}
          {(missing || []).map((s) => (
            <span key={`x${s}`} style={{
              font: `500 11px ${FONT}`, color: C.muted, background: '#f6f5f0',
              border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 7px',
              textDecoration: 'line-through',
            }}>{s}</span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BestFitPanel({ candidateId, limit = 5, defaultOpen = true }) {
  const [rows, setRows] = useState(null);      // null = still asking
  const [open, setOpen] = useState(defaultOpen);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!candidateId) { setRows([]); return undefined; }
    let alive = true;
    setRows(null); setErr('');
    api.get(`/match-view/for-candidate/${encodeURIComponent(candidateId)}?limit=${limit}`)
      .then((r) => { if (alive) setRows(r.data?.results || []); })
      // Rule 2: a failure here must not break the profile page around it.
      .catch((e) => { if (alive) { setErr(errText(e)); setRows([]); } })
      .finally(() => {});
    return () => { alive = false; };
  }, [candidateId, limit]);

  if (rows === null) {
    return (
      <div style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        fontFamily: FONT, marginTop: 16, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 10,
        color: C.muted, font: `400 13px ${FONT}`,
      }}>
        <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
        Scoring this person against the open roles…
      </div>
    );
  }

  // Rule 1 and Rule 2: nothing to show, or the call failed - render nothing.
  if (rows.length === 0 || err) return null;

  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      fontFamily: FONT, marginTop: 16, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', background: C.cream, border: 'none',
          borderBottom: open ? `1px solid ${C.border}` : 'none', cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Target size={17} color={C.goldDark} />
        <span style={{ font: `600 14px ${FONT}`, color: C.ink }}>
          Best fit right now
        </span>
        <span style={{ font: `400 12px ${FONT}`, color: C.muted }}>
          {rows.length} open {rows.length === 1 ? 'role' : 'roles'} · scored just now
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {open ? <ChevronDown size={16} color={C.muted} />
                : <ChevronRight size={16} color={C.muted} />}
        </span>
      </button>

      {open && (
        <div>
          {rows.map((r, i) => (
            <div
              key={r.requirement_id || i}
              style={{
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 13.5px ${FONT}`, color: C.navy }}>
                    {r.position || 'Untitled requirement'}
                  </div>
                  <div style={{ font: `400 12px ${FONT}`, color: C.muted, marginTop: 1 }}>
                    {[r.client, r.location].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <ScoreBadge score={r.match_score} confidence={r.confidence} />
              </div>
              <WhyList
                why={r.why}
                matched={r.matched_skills}
                missing={r.missing_skills}
              />
            </div>
          ))}
          <div style={{
            padding: '9px 16px', background: C.cream,
            borderTop: `1px solid ${C.border}`,
            font: `400 11.5px ${FONT}`, color: C.muted,
          }}>
            Scored live against the open requirements. Nothing here is saved,
            and the older figures on the Matches screen are untouched.
          </div>
        </div>
      )}
    </div>
  );
}
